import { createCanonicalTracker } from './canonicalTracker'
import { computeFaceMetrics } from './faceMath'

const clamp01 = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(1, n))
}

const smoothAttackRelease = (current, target, rise = 0.45, fall = 0.16) => {
  const c = clamp01(current)
  const t = clamp01(target)
  const k = t >= c ? rise : fall
  return c + (t - c) * Math.max(0.01, Math.min(1, Number(k) || 0.2))
}

const createZeroBlendshapeSignals = () => ({
  confidence: 0,
  jawOpen: 0,
  mouthOpen: 0,
  mouthSmileLeft: 0,
  mouthSmileRight: 0,
  mouthPucker: 0,
  mouthFunnel: 0,
  cheekPuff: 0,
  cheekSquintLeft: 0,
  cheekSquintRight: 0,
  eyeBlinkLeft: 0,
  eyeBlinkRight: 0
})

const blendshapeNameScore = (categories = []) => {
  const out = {}
  for (let i = 0; i < categories.length; i += 1) {
    const row = categories[i]
    const key = String(row?.categoryName || row?.displayName || '')
      .trim()
      .toLowerCase()
    if (!key) continue
    out[key] = clamp01(row?.score)
  }
  return out
}

const pickBlendshape = (map, names = []) => {
  let best = 0
  for (let i = 0; i < names.length; i += 1) {
    const key = String(names[i] || '')
      .trim()
      .toLowerCase()
    if (!key) continue
    const score = clamp01(map?.[key])
    if (score > best) best = score
  }
  return best
}

const deriveBlendshapeSignals = (categories = []) => {
  const map = blendshapeNameScore(categories)
  const eyeBlinkLeft = pickBlendshape(map, ['eyeblinkleft'])
  const eyeBlinkRight = pickBlendshape(map, ['eyeblinkright'])
  const jawOpen = pickBlendshape(map, ['jawopen'])
  const mouthOpen = Math.max(jawOpen, pickBlendshape(map, ['mouthopen']))
  const mouthSmileLeft = pickBlendshape(map, ['mouthsmileleft'])
  const mouthSmileRight = pickBlendshape(map, ['mouthsmileright'])
  const mouthPucker = pickBlendshape(map, ['mouthpucker'])
  const mouthFunnel = pickBlendshape(map, ['mouthfunnel'])
  const cheekPuff = pickBlendshape(map, ['cheekpuff'])
  const cheekSquintLeft = pickBlendshape(map, ['cheeksquintleft'])
  const cheekSquintRight = pickBlendshape(map, ['cheeksquintright'])
  const confidence = categories.length > 0 ? 1 : 0
  return {
    confidence,
    jawOpen,
    mouthOpen,
    mouthSmileLeft,
    mouthSmileRight,
    mouthPucker,
    mouthFunnel,
    cheekPuff,
    cheekSquintLeft,
    cheekSquintRight,
    eyeBlinkLeft,
    eyeBlinkRight
  }
}

export function createTracker(options = {}) {
  const {
    getVideoEl,
    getSettings,
    getFaceLandmarker,
    getFaceDetector,
    getCanvasAdapter,
    getAvatarRig
  } = options

  const { resourceManager } = options || {}

  const processCanonical = createCanonicalTracker()

  let running = false
  let lastDetectAt = 0
  let lastProcessedAt = 0
  let lastFaceLandmarks = null
  let lastFaceBox = null
  let baselineCanonical = null
  let baselineFrames = 0
  let prevMotion = null
  let motionCanvas = null
  let motionCtx = null
  let bsBlinkL = 0
  let bsBlinkR = 0
  let blendshapeSignals = createZeroBlendshapeSignals()
  let lastOut = null

  const latest = () => ({
    lastFaceLandmarks,
    lastFaceBox,
    baselineCanonical,
    baselineFrames,
    bsBlinkL,
    bsBlinkR,
    blendshapeSignals,
    lastProcessedAt
  })

  function _ensureMotionCanvas() {
    if (motionCanvas || !getCanvasAdapter) return
    try {
      const adapter = getCanvasAdapter()
      motionCanvas = adapter.createCanvas(64, 48)
      motionCanvas.width = 64
      motionCanvas.height = 48
      motionCtx = adapter.getContext(motionCanvas, '2d', { willReadFrequently: true })
      // register motion canvas with resource manager if provided
      try {
        if (resourceManager && typeof resourceManager.registerCanvas === 'function') {
          resourceManager.registerCanvas(motionCanvas)
          if (typeof resourceManager.registerDisposable === 'function') {
            resourceManager.registerDisposable(() => {
              try {
                motionCanvas.width = 0
                motionCanvas.height = 0
              } catch (err) {
                console.debug('[tracker] motionCanvas zero error', err)
              }
            })
          }
        }
      } catch (err) {
        console.debug('[tracker] registerCanvas/registerDisposable inner error', err)
      }
    } catch (err) {
      console.debug('[tracker] registerCanvas/registerDisposable error', err)
      motionCanvas = null
      motionCtx = null
    }
  }

  async function start() {
    running = true
    _ensureMotionCanvas()
  }

  async function stop() {
    running = false
    // clear motion buffer
    prevMotion = null
    // drop references to internal canvases/contexts to aid GC (resourceManager will zero canvas)
    try {
      motionCtx = null
      motionCanvas = null
    } catch (e) {}
  }

  async function processFrame({ now, canvasWidth = 0, canvasHeight = 0, mouthLevel = 0 } = {}) {
    if (!running) await start()
    const settings = (typeof getSettings === 'function' && getSettings()) || {}
    const videoEl = typeof getVideoEl === 'function' ? getVideoEl() : null
    const faceLandmarker = typeof getFaceLandmarker === 'function' ? getFaceLandmarker() : null
    let faceDetector = typeof getFaceDetector === 'function' ? getFaceDetector() : null

    if (!videoEl) return latest()

    let nowMs = Number.isFinite(now) ? now : performance.now()
    if (nowMs <= lastDetectAt) nowMs = lastDetectAt + 1
    lastProcessedAt = nowMs
    if (nowMs - lastDetectAt < (settings.detectInterval || 120)) {
      return { ...latest(), ...(lastOut || {}) }
    }
    lastDetectAt = nowMs

    // Try MediaPipe face landmarker first
    if (faceLandmarker) {
      try {
        const res = faceLandmarker.detectForVideo(videoEl, nowMs)
        if (res?.faceLandmarks?.length) {
          lastFaceLandmarks = res.faceLandmarks[0]
        }
        // Capture blendshape signals (mouth/cheeks/eyes)
        try {
          const categories = res?.faceBlendshapes?.[0]?.categories || []
          const next = deriveBlendshapeSignals(categories)
          blendshapeSignals = {
            confidence: next.confidence,
            jawOpen: smoothAttackRelease(blendshapeSignals.jawOpen, next.jawOpen, 0.46, 0.16),
            mouthOpen: smoothAttackRelease(blendshapeSignals.mouthOpen, next.mouthOpen, 0.48, 0.18),
            mouthSmileLeft: smoothAttackRelease(
              blendshapeSignals.mouthSmileLeft,
              next.mouthSmileLeft,
              0.38,
              0.2
            ),
            mouthSmileRight: smoothAttackRelease(
              blendshapeSignals.mouthSmileRight,
              next.mouthSmileRight,
              0.38,
              0.2
            ),
            mouthPucker: smoothAttackRelease(
              blendshapeSignals.mouthPucker,
              next.mouthPucker,
              0.42,
              0.18
            ),
            mouthFunnel: smoothAttackRelease(
              blendshapeSignals.mouthFunnel,
              next.mouthFunnel,
              0.42,
              0.18
            ),
            cheekPuff: smoothAttackRelease(blendshapeSignals.cheekPuff, next.cheekPuff, 0.3, 0.16),
            cheekSquintLeft: smoothAttackRelease(
              blendshapeSignals.cheekSquintLeft,
              next.cheekSquintLeft,
              0.36,
              0.2
            ),
            cheekSquintRight: smoothAttackRelease(
              blendshapeSignals.cheekSquintRight,
              next.cheekSquintRight,
              0.36,
              0.2
            ),
            eyeBlinkLeft: smoothAttackRelease(
              blendshapeSignals.eyeBlinkLeft,
              next.eyeBlinkLeft,
              0.52,
              0.22
            ),
            eyeBlinkRight: smoothAttackRelease(
              blendshapeSignals.eyeBlinkRight,
              next.eyeBlinkRight,
              0.52,
              0.22
            )
          }
          bsBlinkL = blendshapeSignals.eyeBlinkLeft
          bsBlinkR = blendshapeSignals.eyeBlinkRight
        } catch (e) {}
      } catch (err) {
        console.debug('[tracker] faceLandmarker.detectForVideo error', err)
      }
    } else {
      // FaceDetector fallback
      if (!faceDetector && typeof window !== 'undefined' && 'FaceDetector' in window) {
        try {
          faceDetector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 })
        } catch (err) {
          console.debug('[tracker] FaceDetector construction error', err)
          faceDetector = null
        }
      }
      if (faceDetector) {
        try {
          const faces = await faceDetector.detect(videoEl)
          if (faces && faces.length) {
            lastFaceBox = faces[0].boundingBox
          }
        } catch (err) {
          console.debug('[tracker] faceDetector.detect error', err)
        }
      } else if (motionCtx) {
        try {
          motionCtx.drawImage(videoEl, 0, 0, motionCanvas.width, motionCanvas.height)
          const frame = motionCtx.getImageData(0, 0, motionCanvas.width, motionCanvas.height)
          if (prevMotion) {
            let total = 0
            let sumX = 0
            let sumY = 0
            let minX = motionCanvas.width
            let minY = motionCanvas.height
            let maxX = 0
            let maxY = 0
            const threshold = 18
            for (let y = 0; y < motionCanvas.height; y += 1) {
              for (let x = 0; x < motionCanvas.width; x += 1) {
                const idx = (y * motionCanvas.width + x) * 4
                const r = frame.data[idx]
                const g = frame.data[idx + 1]
                const b = frame.data[idx + 2]
                const pr = prevMotion.data[idx]
                const pg = prevMotion.data[idx + 1]
                const pb = prevMotion.data[idx + 2]
                const diff = Math.abs(r - pr) + Math.abs(g - pg) + Math.abs(b - pb)
                if (diff > threshold) {
                  total += diff
                  sumX += x * diff
                  sumY += y * diff
                  if (x < minX) minX = x
                  if (y < minY) minY = y
                  if (x > maxX) maxX = x
                  if (y > maxY) maxY = y
                }
              }
            }
            if (total > 1000) {
              const cx = sumX / total
              const cy = sumY / total
              const w = Math.max(6, maxX - minX)
              const h = Math.max(6, maxY - minY)
              const scaleX = videoEl.videoWidth / motionCanvas.width
              const scaleY = videoEl.videoHeight / motionCanvas.height
              const nextBox = {
                x: (cx - w / 2) * scaleX,
                y: (cy - h / 2) * scaleY,
                width: w * scaleX,
                height: h * scaleY
              }
              if (lastFaceBox) {
                const alpha = 0.35
                lastFaceBox = {
                  x: lastFaceBox.x + (nextBox.x - lastFaceBox.x) * alpha,
                  y: lastFaceBox.y + (nextBox.y - lastFaceBox.y) * alpha,
                  width: lastFaceBox.width + (nextBox.width - lastFaceBox.width) * alpha,
                  height: lastFaceBox.height + (nextBox.height - lastFaceBox.height) * alpha
                }
              } else {
                lastFaceBox = nextBox
              }
            }
          }
          prevMotion = frame
        } catch (err) {
          console.debug('[tracker] motion detection loop error', err)
        }
      }
    }

    // Compute faceMetrics and canonical transforms via canonical tracker
    const avatarRig = typeof getAvatarRig === 'function' ? getAvatarRig() : null
    const vw = videoEl.videoWidth || canvasWidth || 0
    const vh = videoEl.videoHeight || canvasHeight || 0
    let faceMetrics = null
    if (lastFaceLandmarks) {
      try {
        faceMetrics = computeFaceMetrics(lastFaceLandmarks, vw, vh)
      } catch (e) {
        faceMetrics = null
      }
    }

    const out = processCanonical({
      lastFaceLandmarks,
      faceMetrics,
      canvasWidth: canvasWidth || vw,
      canvasHeight: canvasHeight || vh,
      avatarRig,
      settings,
      baselineCanonical,
      baselineFrames,
      mouthLevel
    })
    baselineCanonical = out.baselineCanonical
    baselineFrames = out.baselineFrames
    lastOut = out

    return {
      ...latest(),
      ...out,
      lastFaceLandmarks,
      lastFaceBox
    }
  }

  function getLatestState() {
    return { ...latest(), ...(lastOut || {}) }
  }

  return { start, stop, processFrame, getLatestState }
}

export default { createTracker }
